/**
 * useChatRetryOnReconnect — watches `isOnline` and bridges to the two
 * Flutter `onInternetConnectivityChanged` branches (`chat_screen.dart:626-644`):
 *
 *  - `false → true` (reconnect): re-claim presence + replay failed text sends.
 *  - `true → false` (disconnect): release presence so messages received during
 *    the disconnect aren't suppressed on the producer side (the
 *    `chattingWith` field is read by Notifications downstream).
 *
 * Additionally, this hook listens for tab-visibility (`visibilitychange`) and
 * window-focus events and triggers `retryAllFailed` whenever the user
 * re-engages the page while online. Rationale (Round 3 / Finding 8): on some
 * platforms (notably macOS with manual WiFi toggling) the browser does not
 * reliably emit the `online` event when connectivity is restored, so a user
 * who comes back to the tab after reconnecting would otherwise see failed
 * bubbles never auto-retry. This catch-up matches what Flutter's app-
 * lifecycle observers do (every `resumed` cycle replays failed sends, see
 * `chat_screen.dart:131-153` / `:626-644`).
 *
 * Extracted from `useChat.ts` so the two-transition logic is named and the
 * main hook drops below the size threshold (`presentation.md`).
 */
import { useEffect, useRef } from 'react';

import { subscribeToTabVisibility } from '@/presentation/utils/tabVisibility';

export interface UseChatRetryOnReconnectArgs {
  isOnline: boolean;
  /** Dispatch `setChattingWith(peerId)`. Called on reconnect. */
  claimPresence: () => void;
  /** Dispatch `setChattingWith('')`. Called on disconnect. */
  releasePresence: () => void;
  /** Re-issue every failed text message via the use case. */
  retryAllFailed: () => Promise<void>;
}

export const useChatRetryOnReconnect = (
  args: UseChatRetryOnReconnectArgs,
): void => {
  const { isOnline, claimPresence, releasePresence, retryAllFailed } = args;
  const prevIsOnlineRef = useRef<boolean>(isOnline);

  // Keep the latest `retryAllFailed` reachable from the
  // visibility/focus listeners without re-registering them on every render.
  const retryAllFailedRef = useRef(retryAllFailed);
  retryAllFailedRef.current = retryAllFailed;

  // Same for the `isOnline` value — the visibility / focus handler reads it
  // synchronously to gate the retry call (no point firing it offline).
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;

  useEffect(() => {
    const prev = prevIsOnlineRef.current;
    prevIsOnlineRef.current = isOnline;
    // Mirror `chat_screen.dart:626-644` exactly — both transitions:
    if (!prev && isOnline) {
      claimPresence();
      void retryAllFailed();
    } else if (prev && !isOnline) {
      releasePresence();
    }
  }, [isOnline, claimPresence, releasePresence, retryAllFailed]);

  // Visibility + focus catch-up. Fires whenever the user re-engages the tab
  // / window — covers the case where the browser missed the `online` event
  // during the offline period (Round 3 / Finding 8). Gated on `isOnline`
  // so we don't fire a no-op retry while still disconnected.
  useEffect(() => {
    const triggerCatchUp = (): void => {
      if (!isOnlineRef.current) return;
      void retryAllFailedRef.current();
    };

    const unsubscribeVisibility = subscribeToTabVisibility((isVisible) => {
      if (isVisible) triggerCatchUp();
    });

    if (typeof window === 'undefined') {
      return () => {
        unsubscribeVisibility();
      };
    }

    window.addEventListener('focus', triggerCatchUp);
    return () => {
      unsubscribeVisibility();
      window.removeEventListener('focus', triggerCatchUp);
    };
  }, []);
};
